import axios from "axios";
import { X } from "lucide-react";
import { useState } from "react";
import { ImSpinner2 } from "react-icons/im";
import {
  convertTo12Hour,
  convertTo24Hour,
} from "../../../utils/formatDateTime";
import { showError, showSuccess } from "../../../utils/toastUtils";

const EditSlotModal = ({ slot, onClose, onSuccess, branchId }) => {
  const DAYS = [
    { label: "Sun", value: 0 },
    { label: "Mon", value: 1 },
    { label: "Tue", value: 2 },
    { label: "Wed", value: 3 },
    { label: "Thu", value: 4 },
    { label: "Fri", value: 5 },
    { label: "Sat", value: 6 },
  ];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startTime, setStartTime] = useState(
    slot ? convertTo12Hour(slot.startTime) : "",
  );
  const [endTime, setEndTime] = useState(
    slot ? convertTo12Hour(slot.endTime) : "",
  );
  const [activeDays, setActiveDays] = useState(
    slot?.activeDays || [0, 1, 2, 3, 4, 5, 6],
  );

  const toggleDay = (day) => {
    setActiveDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  const parseTimeInput = (input) => {
    // Handle formats: "9:00 AM", "09:00 AM", "9:00AM", "900AM", etc.
    input = input.trim().toUpperCase();

    // Add space before AM/PM if missing
    input = input.replace(/(\d)(AM|PM)/, "$1 $2");

    let match = input.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/);
    if (!match) return "";

    let hours = parseInt(match[1]);
    let minutes = match[2] ? parseInt(match[2]) : 0;
    let period = match[3] || (hours >= 12 ? "PM" : "AM");

    // Validate
    if (hours > 12 || minutes > 59) return "";

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")} ${period}`;
  };

  const submit = async () => {
    const parsedStart = parseTimeInput(startTime);
    const parsedEnd = parseTimeInput(endTime);

    if (!parsedStart || !parsedEnd) {
      showError("Please enter valid times (e.g., 9:00 AM)");
      return;
    }

    if (activeDays.length === 0) {
      showError("Please select at least one active day");
      return;
    }

    const payload = {
      branchId,
      startTime: convertTo24Hour(parsedStart),
      endTime: convertTo24Hour(parsedEnd),
      activeDays,
      status: slot?.status || "active",
    };
    // console.log("Payload:", payload);
    try {
      setIsSubmitting(true);
      if (slot) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/slot-bookings/${slot._id}`,
          payload,
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/slot-bookings/create`,
          payload,
        );
      }

      showSuccess(slot ? "Slot updated" : "Slot created");
      onSuccess(slot ? { ...slot, ...payload } : payload);
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to save slot");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        <div className="sticky tracking-wide top-0 bg-accentTextColor/70 text-white px-6 py-5 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">
              {slot ? "Edit Slot" : "Add New Slot"}
            </h2>
            <X
              onClick={onClose}
              className="size-6 active:scale-[0.98] hover:scale-[1.02] cursor-pointer"
            />
          </div>
          <p className="text-sm mt-1">Configure time slot settings</p>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Start Time
              </label>
              <input
                type="text"
                placeholder="9:00 AM"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full rounded-lg px-4 py-3 outline-none ring-1 ring-brandAccentColor focus:ring-primaryColor transition-all duration-200"
              />
              <p className="text-xs text-gray-500 mt-1">Format: 9:00 AM</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                End Time
              </label>
              <input
                type="text"
                placeholder="5:00 PM"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full rounded-lg px-4 py-3 outline-none ring-1 ring-brandAccentColor focus:ring-primaryColor transition-all duration-200"
              />
              <p className="text-xs text-gray-500 mt-1">Format: 5:00 PM</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Active Days
            </label>
            <div className="grid grid-cols-7 gap-2 text-center">
              {DAYS.map((day) => {
                const isActive = activeDays.includes(day.value);
                return (
                  <button
                    key={day.value}
                    onClick={() => toggleDay(day.value)}
                    className={`py-2 rounded-lg text-xs font-medium transition ${
                      isActive
                        ? "bg-[#F7BFAD] text-[#f0653a] border border-[#f0653a]/40 shadow-sm"
                        : "bg-gray-100 text-gray-400 border border-gray-200"
                    } active:scale-[0.98] transform-gpu`}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold 
             hover:bg-gray-50 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-[#f0653a] to-[#d94f2a] 
             text-white font-semibold active:scale-[0.98] transition-all duration-200
             disabled:opacity-80 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <ImSpinner2 className="w-5 h-5 animate-spin" />
                {slot ? "Updating..." : "Creating..."}
              </>
            ) : (
              `${slot ? "Update" : "Create"} Slot`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
export default EditSlotModal;
